import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./FactoriesAndLabs.module.scss";
import { getCDNImageUrl } from "@/utils/Common";

const warehouses = [
  {
    id: 1,
    city: "Mumbai",
    country: "India",
    phoneNumber: "+91 22 1234 5678",
    image: getCDNImageUrl("Factory-1.webp"),
  },
  {
    id: 2,
    city: "Shanghai",
    country: "China",
    phoneNumber: "+86 21 8765 4321",
    image: getCDNImageUrl("Factory-2.webp"),
  },
  {
    id: 3,
    city: "Dubai",
    country: "UAE",
    phoneNumber: "+971 4 987 6543",
    image: getCDNImageUrl("Factory-3.webp"),
  },
];

// Desktop Carousel Component
const DesktopCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [windowWidth, setWindowWidth] = useState(1200); // Default width for SSR

  // Handle window resize and initial width
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    // Set initial width
    setWindowWidth(window.innerWidth);

    // Add resize listener
    window.addEventListener("resize", handleResize);

    // Cleanup
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Auto-scroll functionality
  useEffect(() => {
    if (!isHovered) {
      const interval = setInterval(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % warehouses.length);
      }, 4000);

      return () => clearInterval(interval);
    }
  }, [isHovered]);

  const handleCardHover = (index) => {
    setHoveredIndex(index);
    setIsHovered(true);
  };

  const handleCardLeave = () => {
    setHoveredIndex(null);
    setIsHovered(false);
  };

  const getCardWidths = () => {
    // Calculate available width (subtract gaps only)
    const gap = windowWidth <= 768 ? 16 : 32; // Smaller gaps on mobile
    const totalGaps = gap * 2; // 2 gaps between 3 cards
    const availableWidth = windowWidth - totalGaps;

    // Distribute width: expanded card gets 50%, collapsed cards get 25% each
    let expandedWidth = Math.floor(availableWidth * 0.5);
    let collapsedWidth = Math.floor(availableWidth * 0.25);

    // Add constraints for better responsiveness
    if (windowWidth <= 768) {
      // On mobile, use the existing mobile carousel
      expandedWidth = 280;
      collapsedWidth = 280;
    } else {
      // Desktop constraints - only minimum sizes
      expandedWidth = Math.max(350, expandedWidth); // Min 350px only
      collapsedWidth = Math.max(200, collapsedWidth); // Min 200px only
    }

    return { expandedWidth, collapsedWidth };
  };

  const getTranslateX = () => {
    const { expandedWidth, collapsedWidth } = getCardWidths();
    const gap = windowWidth <= 768 ? 16 : 32; // Smaller gaps on mobile

    // Calculate the position needed to center the current card
    const cardPosition = currentIndex * (collapsedWidth + gap);

    // Calculate the offset to center the expanded card in the viewport
    const viewportCenter = windowWidth / 2;
    const expandedCardCenter = expandedWidth / 2;
    const centerOffset = viewportCenter - expandedCardCenter;

    return centerOffset - cardPosition;
  };

  const carouselVariants = {
    animate: {
      x: getTranslateX(),
      transition: {
        duration: 0.8,
        ease: "easeInOut",
      },
    },
  };

  const getCardVariants = (index) => {
    const { expandedWidth, collapsedWidth } = getCardWidths();

    return {
      expanded: {
        width: `${expandedWidth}px`,
        height: "40vw",
        y: 0,
        transition: {
          duration: 0.5,
          ease: "easeInOut",
        },
      },
      collapsed: {
        width: `${collapsedWidth}px`,
        height: "30vw",
        y: 40,
        transition: {
          duration: 0.5,
          ease: "easeInOut",
        },
      },
    };
  };

  const isExpanded = (index) => {
    if (isHovered) {
      return index === hoveredIndex;
    }
    return index === currentIndex;
  };

  return (
    <div className={styles.carouselTrack}>
      <motion.div
        className={styles.carouselWrapper}
        variants={carouselVariants}
        animate="animate"
      >
        {warehouses.map((warehouse, index) => {
          const shouldExpand = isExpanded(index);

          return (
            <motion.div
              key={warehouse.id}
              className={`${styles.carouselCard} ${
                shouldExpand ? styles.expanded : styles.collapsed
              }`}
              variants={getCardVariants(index)}
              animate={shouldExpand ? "expanded" : "collapsed"}
              onMouseEnter={() => handleCardHover(index)}
              onMouseLeave={handleCardLeave}
              style={{
                backgroundImage: `url(${warehouse.image})`,
              }}
            >
              <div className={styles.cardOverlay}>
                <div className={styles.cardContent}>
                  <AnimatePresence mode="wait">
                    {shouldExpand && (
                      <motion.div
                        key={`desktop-content-${index}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{
                          duration: 0.4,
                          ease: "easeOut",
                          delay: 0.6, // Delay to wait for card expansion to finish
                        }}
                      >
                        <h3 className={styles.cardTitle}>
                          At {warehouse.city}, {warehouse.country}
                        </h3>
                        <p className={styles.cardInfo}>
                          {warehouse.phoneNumber}
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </div>
            </motion.div>
          );
        })}
      </motion.div>
    </div>
  );
};

// Simple Mobile Carousel Component - Column Layout
const MobileCarousel = () => {
  return (
    <div className={styles.carouselTrack}>
      <div className={styles.carouselWrapper}>
        {warehouses.map((warehouse, index) => (
          <div
            key={warehouse.id}
            className={`${styles.carouselCard} ${styles.mobile}`}
            style={{
              backgroundImage: `url(${warehouse.image})`,
            }}
          >
            <div className={styles.cardOverlay}>
              <div className={styles.cardContent}>
                <h3 className={styles.cardTitle}>
                  At {warehouse.city}, {warehouse.country}
                </h3>
                <p className={styles.cardInfo}>{warehouse.phoneNumber}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

function FactoriesAndLabs() {
  return (
    <div className={styles.factoriesAndLabs}>
      {/* Heading Container */}
      <div className={styles.headingContainer}>
        {/* Small Heading */}
        <h6>ELCHEMY INSIGHTS</h6>
        {/* Big Heading */}
        <h2>Factories and R&D Labs</h2>
      </div>

      {/* Carousel Container */}
      <div className={styles.carouselContainer}>
        <div className={styles.desktopCarousel}>
          <DesktopCarousel />
        </div>
        <div className={styles.mobileCarousel}>
          <MobileCarousel />
        </div>
      </div>
    </div>
  );
}

export default FactoriesAndLabs;
